import { validateAndRefreshToken } from '../utils/tokenUtils';
import { config } from '../config';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/** What every apiService call rejects with when the response is not 2xx. */
export interface ApiError {
  success: false;
  error: string;
  message: string;
  /** HTTP status, so a caller can tell a 401 (signed out) from a 403 (not allowed). */
  status: number;
}

export interface SpeechRateSettings {
  enabled: boolean;
  /** Words per minute above which a rep is warned. */
  wpm_threshold: number;
  /** LEGACY, still sent by a backend that predates the WPM change. Read only
   *  as a fallback; both fields carry a WPM number during the rollout. */
  max_words?: number;
  time_window?: number;
}

/** A landmine result's guidance, as the live landmine alert shows it. */
export interface LandmineKnowledgeDetails {
  quickTips: string;
  canSay: string[];
  cannotSay: string[];
}

/** A competitor result's guidance, as the live competitor alert shows it. */
export interface CompetitorKnowledgeDetails {
  talkingPoints: string;
  advantages: string[];
  weaknesses: string[];
}

interface LiveKnowledgeSearchResultBase {
  _id: string;
  label: string;
  answer: string;
  category: string;
  score: number;
}

/**
 * One Knowledge Search answer. Landmine and competitor results carry their
 * guidance in `details`; an older backend sends none, and the card falls back
 * to the flat `answer`.
 */
export type LiveKnowledgeSearchResult =
  | (LiveKnowledgeSearchResultBase & { type: 'objection' | 'faq'; details?: undefined })
  | (LiveKnowledgeSearchResultBase & { type: 'landmine'; details?: LandmineKnowledgeDetails })
  | (LiveKnowledgeSearchResultBase & { type: 'competitor'; details?: CompetitorKnowledgeDetails });

export interface LiveKnowledgeSearchResponse {
  results: LiveKnowledgeSearchResult[];
  isPartial: boolean;
  /** Types whose source was down; the rest were searched. Empty from an older backend. */
  unavailableTypes: LiveKnowledgeSearchResult['type'][];
}

class ApiService {
  private baseURL: string;
  
  constructor() {
    this.baseURL = config.server.backendUrl;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const { token } = await validateAndRefreshToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    let data: any;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    
    if (!response.ok) {
      const error: ApiError = {
        success: false,
        error: data?.error || 'API_ERROR',
        message: data?.message || `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
      };
      throw error;
    }
    
    return data;
  }

  async get<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API GET request failed:', error);
      throw error;
    }
  }

  /** Searches one knowledge type (Knowledge Search asks for each type on its own) or `all`. */
  async searchLiveKnowledge(
    query: string,
    type: LiveKnowledgeSearchResult['type'] | 'all',
  ): Promise<LiveKnowledgeSearchResponse> {
    const params = new URLSearchParams({ q: query, type, limit: '20' });
    const response = await this.get<Partial<LiveKnowledgeSearchResponse>>(
      `/live-intelligence/search?${params.toString()}`
    );
    return {
      results: response.data?.results || [],
      isPartial: response.data?.isPartial || false,
      unavailableTypes: response.data?.unavailableTypes || [],
    };
  }

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API PUT request failed:', error);
      throw error;
    }
  }

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API POST request failed:', error);
      throw error;
    }
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API PATCH request failed:', error);
      throw error;
    }
  }

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();

      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });

      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API DELETE request failed:', error);
      throw error;
    }
  }

  // Multipart form-data POST/PATCH — browser sets the Content-Type + boundary automatically
  async postFormData<T = any>(endpoint: string, formData: FormData, method: 'POST' | 'PATCH' = 'PATCH'): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const { token } = await validateAndRefreshToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method,
        headers,
        body: formData,
      });

      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API form-data request failed:', error);
      throw error;
    }
  }

  async submitEntryFeedback(sessionId: string, payload: {
    entryType: string;
    entryTimestamp: string;
    category?: string;
    rating: 'up' | 'down';
    reason?: string;
  }): Promise<ApiResponse> {
    return this.patch(`/conversations/sessions/${sessionId}/entry-feedback`, payload);
  }

  async submitSessionFeedback(sessionId: string, formData: FormData): Promise<ApiResponse> {
    return this.postFormData(`/conversations/sessions/${sessionId}/general-feedback`, formData);
  }

  /** Tenant-wide feedback (no session required) — writes to the `feedbacks` collection,
   *  the same API admin-portal's Send Feedback uses. Requires `title` + `description`
   *  fields on the FormData, plus optional `attachments` files. */
  async submitFeedback(formData: FormData): Promise<ApiResponse> {
    return this.postFormData('/admin/feedback', formData, 'POST');
  }
}

export const apiService = new ApiService();
export const apiClient = apiService; // Alias for compatibility

export const speechRateApi = {
  getSettings: () => apiService.get<SpeechRateSettings>('/speech-rate'),
  // Update settings is admin-only, not available in frontend
};

// Sales Playbook API
export interface SalesPlaybookRoleInfo {
  name: string;
  displayName: string;
}

export interface SalesPlaybookInfo {
  playbookName: string;
  displayName: string;
  roles: SalesPlaybookRoleInfo[];
}

export interface SalesPlaybookQuestion {
  _id: string;
  questionText: string;
  tags: string[];
  sortOrder: number;
  isActive: boolean;
}

export interface RoleData {
  playbookName: string;
  playbookDisplayName: string;
  roleId: string;
  roleName: string;
  roleDisplayName: string;
  pitch: string[];
  advanced: string[];
  questions: SalesPlaybookQuestion[];
}

export const salesPlaybookApi = {
  getPlaybooks: async (): Promise<SalesPlaybookInfo[]> => {
    const response = await apiService.get<SalesPlaybookInfo[]>('/salesplaybooks/playbooks');
    if (response?.success && response?.data) {
      return response.data;
    }
    if (Array.isArray(response)) {
      return response as unknown as SalesPlaybookInfo[];
    }
    return [];
  },

  getRoleData: async (playbookName: string, roleName: string): Promise<RoleData | null> => {
    const response = await apiService.get<RoleData>(`/salesplaybooks/${playbookName}/roles/${roleName}`);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as unknown as RoleData;
  }
};

// Backward compatibility aliases
export type TalkTrackRoleInfo = SalesPlaybookRoleInfo;
export type TalkTrackQuestion = SalesPlaybookQuestion;
export const talkTracksApi = salesPlaybookApi;
